import { useEffect, useRef, useState } from 'react';
import { Search, X } from '../../icons';
import { FileTypeGlyph } from '../../components/FileTypeGlyph';
import { KIND_GLYPH } from '../../lib/workspace-kind-glyph';
import { FILE_KINDS, type FileKind } from '../../lib/file-kind';
import { tWorkspace, type WorkspaceI18nKey } from '../../lib/workspace-i18n';

/**
 * 칩 아이콘 (2026-08-14, 2026-08-18 그림 교체) — **파일 목록·만들기 메뉴와 같은 그림**을 쓴다.
 *
 * 2026-08-14 에는 lucide 선 아이콘 + 유형색(`--k-*`)이었다. 2026-08-18 에 목록 아이콘이
 * 브랜드 그림으로 바뀌면서 칩만 선 아이콘으로 남으면 "무엇을 거른 것인지" 를 눈으로 잇기
 * 어려워져 함께 옮겼다. 표는 `lib/workspace-kind-glyph` 한 곳에 있다(칩·만들기 메뉴 공용).
 *
 * ⚠ **그림은 색을 상속하지 않는다.** 브랜드 색이 그림 안에 박혀 있어 그전처럼 `--k-*` 를
 *   style 로 물리지 않는다. 선택된 칩(배경 반전)에서도 같은 그림을 그대로 쓴다 — 흰 문서지가
 *   어두운 배경에서 오히려 잘 보인다.
 *
 * '전체' 는 파일 유형이 아니라 **필터 해제**라서 그림을 주지 않는다.
 */
/** 칩 그림 크기 — 12px 글자 옆에서 읽히는 하한. 칩 높이(29px)는 이 값에 안 바뀐다. */
const CHIP_GLYPH_SIZE = 15;

/**
 * 칩 라벨 — 서버 FILE_KINDS 순서를 그대로 따른다
 * (전체/Word/Excel/PPT/PDF/마크업/txt/이미지/그외).
 *
 * ⚠ Record<FileKind, …> 이므로 FILE_KINDS 에 유형을 더하면 **여기가 타입 에러로 막는다.**
 *   라벨 없는 칩이 조용히 빈 칸으로 뜨는 것을 방지하는 의도적 결합이다.
 */
const KIND_LABEL_KEY: Record<FileKind, WorkspaceI18nKey> = {
  all: 'workspace.kind.all',
  word: 'workspace.kind.word',
  excel: 'workspace.kind.excel',
  ppt: 'workspace.kind.ppt',
  hwp: 'workspace.kind.hwp',
  pdf: 'workspace.kind.pdf',
  markup: 'workspace.kind.markup',
  txt: 'workspace.kind.txt',
  image: 'workspace.kind.image',
  etc: 'workspace.kind.etc',
};

/** 입력 후 서버 조회까지 기다리는 시간. 타이핑마다 요청하면 rate-limit 에 걸린다. */
const DEBOUNCE_MS = 300;

interface WorkspaceFilterBarProps {
  kind: FileKind;
  onKindChange: (kind: FileKind) => void;
  /** 확정된(디바운스된) 검색어 — 부모가 서버 조회에 쓴다. */
  query: string;
  onQueryChange: (q: string) => void;
  /** 검색 중이면 "전체 스토리지에서 검색 중" 안내를 띄운다. */
  searching: boolean;
}

/**
 * 워크스페이스 필터 바 (2026-08-12) — 사용자 요구 ①② 를 담당한다.
 *   ① 전체 / Word / Excel / PPT / 그외 유형 필터
 *   ② 파일명 · 문서 제목 검색
 *
 * 검색어는 300ms 디바운스 후에만 부모로 올린다. 입력 자체는 즉시 반영되므로(로컬 state)
 * 타이핑 체감은 그대로다.
 *
 * ⚠ 서버는 검색어가 있으면 현재 폴더가 아니라 전체 스토리지를 재귀 검색한다. 사용자가
 *   "폴더 안인데 왜 다른 게 나오지" 라고 느끼지 않도록 안내 문구를 함께 보여준다.
 */
export function WorkspaceFilterBar({
  kind, onKindChange, query, onQueryChange, searching,
}: WorkspaceFilterBarProps) {
  const [draft, setDraft] = useState(query);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 부모가 검색어를 초기화(예: 폴더 이동)하면 입력창도 따라간다.
  useEffect(() => { setDraft(query); }, [query]);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  function handleInput(value: string) {
    setDraft(value);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onQueryChange(value.trim()), DEBOUNCE_MS);
  }

  function clear() {
    if (timerRef.current) clearTimeout(timerRef.current);
    setDraft('');
    onQueryChange('');
  }

  return (
    <div className="workspace-filterbar">
      <div className="workspace-search">
        <Search size={14} aria-hidden />
        <input
          type="search"
          value={draft}
          placeholder={tWorkspace('workspace.search.placeholder')}
          aria-label={tWorkspace('workspace.search.aria')}
          onChange={(e) => handleInput(e.target.value)}
          onKeyDown={(e) => {
            // Enter 는 디바운스를 건너뛰고 즉시 조회, Esc 는 초기화.
            if (e.key === 'Enter') {
              if (timerRef.current) clearTimeout(timerRef.current);
              onQueryChange(draft.trim());
            } else if (e.key === 'Escape' && draft !== '') {
              e.preventDefault();
              clear();
            }
          }}
        />
        {draft !== '' && (
          <button
            type="button"
            className="workspace-search-clear"
            onClick={clear}
            aria-label={tWorkspace('workspace.search.clear')}
          >
            <X size={13} />
          </button>
        )}
      </div>

      <div className="workspace-kind-chips" role="tablist" aria-label={tWorkspace('workspace.filter.aria')}>
        {FILE_KINDS.map((k) => {
          const glyph = KIND_GLYPH[k];
          const active = kind === k;
          return (
            <button
              key={k}
              type="button"
              role="tab"
              aria-selected={active}
              className={`workspace-kind-chip ${active ? 'workspace-kind-chip--active' : ''}`}
              onClick={() => onKindChange(k)}
            >
              {glyph && <FileTypeGlyph name={glyph} size={CHIP_GLYPH_SIZE} />}
              {tWorkspace(KIND_LABEL_KEY[k])}
            </button>
          );
        })}
      </div>

      {searching && (
        <span className="workspace-search-scope" role="status">
          {tWorkspace('workspace.search.scope_hint')}
        </span>
      )}
    </div>
  );
}
